import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { AuthContext, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye, EyeOff, Shield, Lock } from 'lucide-react';

export default function EnumManager() {
  const { token } = React.useContext(AuthContext);
  const [enums, setEnums] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('job_categories');
  const [showModal, setShowModal] = useState(false);
  const [editingEnum, setEditingEnum] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    value: '',
    label: '',
    order: 0,
    show_security_military: false,
    show_criminal_record: false,
    show_disability: true,
    show_diversity: true
  });

  const categories = [
    'job_categories',
    'visa_types',
    'document_types',
    'skills',
    'job_types',
    'proficiency_levels',
    'languages',
    'industries'
  ];

  useEffect(() => {
    fetchEnums();
  }, []);

  const fetchEnums = async () => {
    try {
      const response = await axios.get(`${API}/admin/enums`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnums(response.data);
    } catch (error) {
      toast.error('Failed to fetch enums');
    }
  };

  const handleCreate = () => {
    setEditingEnum(null);
    setFormData({
      category: selectedCategory,
      value: '',
      label: '',
      order: 0,
      show_security_military: false,
      show_criminal_record: false,
      show_disability: true,
      show_diversity: true
    });
    setShowModal(true);
  };

  const handleEdit = (enumItem) => {
    setEditingEnum(enumItem);
    setFormData({
      category: enumItem.category,
      value: enumItem.value,
      label: enumItem.label,
      order: enumItem.order,
      show_security_military: enumItem.show_security_military || false,
      show_criminal_record: enumItem.show_criminal_record || false,
      show_disability: enumItem.show_disability !== undefined ? enumItem.show_disability : true,
      show_diversity: enumItem.show_diversity !== undefined ? enumItem.show_diversity : true
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEnum) {
        await axios.put(`${API}/admin/enums/${editingEnum.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Enum updated');
      } else {
        await axios.post(`${API}/admin/enums`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Enum created');
      }
      setShowModal(false);
      fetchEnums();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save enum');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this option?')) return;
    try {
      await axios.delete(`${API}/admin/enums/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Enum deleted');
      fetchEnums();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/enums/${id}/publish?is_published=${!currentStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentStatus ? 'Unpublished' : 'Published');
      fetchEnums();
    } catch (error) {
      toast.error('Failed to toggle publish');
    }
  };

  const currentEnums = enums[selectedCategory] || [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Dynamic Enums/Options Manager</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map(cat => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat)}
                className="capitalize"
              >
                {cat.replace('_', ' ')}
              </Button>
            ))}
          </div>

          {/* Add Button */}
          <div className="mb-4">
            <Button onClick={handleCreate} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Add New Option
            </Button>
          </div>

          {/* Enums Table */}
          <div className="space-y-2">
            {currentEnums.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No options yet</p>
            ) : (
              currentEnums.map(item => (
                <div key={item.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm text-gray-500">#{item.order}</span>
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="text-sm text-gray-500">{item.value}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.is_published ? (
                        <Badge className="bg-green-100 text-green-700">Published</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700">Draft</Badge>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleTogglePublish(item.id, item.is_published)}>
                        {item.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(item)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>

                  {/* Show section visibility badges for job_categories */}
                  {item.category === 'job_categories' && (
                    <div className="flex flex-wrap gap-2 mt-2 pl-12">
                      {item.show_security_military && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
                          <Shield className="w-3 h-3 mr-1" />
                          Security & Military
                        </Badge>
                      )}
                      {item.show_criminal_record && (
                        <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-300">
                          <Lock className="w-3 h-3 mr-1" />
                          Criminal Record & DBS
                        </Badge>
                      )}
                      {item.show_disability && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
                          Disability (Voluntary)
                        </Badge>
                      )}
                      {item.show_diversity && (
                        <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-300">
                          Diversity Monitoring
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{editingEnum ? 'Edit' : 'Create'} Enum Option</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Category</Label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    disabled={!!editingEnum}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat} className="capitalize">
                        {cat.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Value (Internal)</Label>
                  <Input
                    value={formData.value}
                    onChange={(e) => setFormData({...formData, value: e.target.value})}
                    placeholder="e.g., certificate"
                    required
                  />
                </div>
                <div>
                  <Label>Label (Display)</Label>
                  <Input
                    value={formData.label}
                    onChange={(e) => setFormData({...formData, label: e.target.value})}
                    placeholder="e.g., Certificate"
                    required
                  />
                </div>
                <div>
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                    required
                  />
                </div>

                {/* Section Visibility Flags for Job Categories */}
                {formData.category === 'job_categories' && (
                  <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="font-medium text-sm text-blue-900">Section Visibility Settings</p>
                    <p className="text-xs text-blue-700 mb-3">Choose which sections to show when this category is selected</p>
                    
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <Checkbox
                        checked={formData.show_security_military}
                        onCheckedChange={(checked) => setFormData({...formData, show_security_military: checked})}
                      />
                      <div className="flex items-center">
                        <Shield className="w-4 h-4 mr-2 text-blue-600" />
                        <span className="text-sm">Show Security & Military Section</span>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 cursor-pointer">
                      <Checkbox
                        checked={formData.show_criminal_record}
                        onCheckedChange={(checked) => setFormData({...formData, show_criminal_record: checked})}
                      />
                      <div className="flex items-center">
                        <Lock className="w-4 h-4 mr-2 text-blue-600" />
                        <span className="text-sm">Show Criminal Record & DBS Section</span>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 cursor-pointer">
                      <Checkbox
                        checked={formData.show_disability}
                        onCheckedChange={(checked) => setFormData({...formData, show_disability: checked})}
                      />
                      <span className="text-sm">Show Disability Section (Voluntary)</span>
                    </label>

                    <label className="flex items-center space-x-3 cursor-pointer">
                      <Checkbox
                        checked={formData.show_diversity}
                        onCheckedChange={(checked) => setFormData({...formData, show_diversity: checked})}
                      />
                      <span className="text-sm">Show Diversity Monitoring Section</span>
                    </label>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">Save</Button>
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}